import { Alert, Card, LearnMoreLink, Page, Text } from '@ui/themed';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { SparkSuspense } from '../../../components/spark-suspense';
import { StaffOnly } from '../../../components/staff-only';
import { isShopify, platformLabel, useEnv } from '../../../context/env-context';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { platformCustomerUrl } from '../../../lib/platform-links';
import { useCustomerGroups } from '../../customer-groups/hooks/use-customer-groups';
import { CustomerAddressesCard } from '../components/customer-addresses-card';
import { CustomerAdvancedDetailsCard } from '../components/customer-advanced-details-card';
import { CustomerCompanyUsersCard } from '../components/customer-company-users-card';
import { CustomerFinancialsCard } from '../components/customer-financials-card';
import { CustomerIdentifierCard } from '../components/customer-identifier-card';
import { CustomerRoleSelectionCard } from '../components/customer-role-selection-card';
import { OptionalDetail } from '../components/optional-detail';
import { TwoColumnLayout } from '../components/two-column-layout';
import { useGetCustomer } from '../hooks/use-get-customer';
import type { Customer } from '../types/customer';

export const CustomerDetailsPage = withPageProviders(CustomerDetailsWrapper);

function CustomerDetailsWrapper() {
  const { navigationHandler } = useHandlers();
  const { id } = navigationHandler.params();

  const {
    data: customer,
    isLoading,
    isError,
    refetch,
  } = useGetCustomer({ identifier: id ?? '', by: 'sparklayer' });

  if (!customer) {
    return (
      <Page narrow>
        <SparkSuspense
          isLoading={isLoading}
          isError={isError}
          errorMessage={t('errors:generic')}
          onReload={() => void refetch()}
        />
      </Page>
    );
  }

  return <CustomerDetailsContent customer={customer} />;
}

const VALID_PAGE_SLUGS = ['list', 'agents'];

function CustomerDetailsContent({ customer }: { customer: Customer }) {
  const { data: customerGroups } = useCustomerGroups();
  const { navigationHandler } = useHandlers();
  const { platform, store_id } = useEnv();
  const pageSlug =
    navigationHandler
      .path()
      .split('/')
      .filter(Boolean)
      .find((breadcrumb) => VALID_PAGE_SLUGS.includes(breadcrumb)) ?? VALID_PAGE_SLUGS[0];

  return (
    <Page
      title={[customer.first_name, customer.last_name].filter(Boolean).join(' ') || customer.email}
      narrow
      backUrl={`/v2/customers/${pageSlug}`}
      primaryAction={
        isShopify(platform) && customer.external_id
          ? {
              icon: 'externalLink',
              label: t('features:customers.pages.customer_details_page.view_in_platform', {
                platform: platformLabel(platform),
              }),
              onClick: () =>
                window.open(
                  platformCustomerUrl(platform, store_id, customer.external_id) ?? '',
                  '_blank',
                  'noopener,noreferrer',
                ),
            }
          : undefined
      }
      description={
        <>
          {t('features:customers.pages.customer_details_page.description')}{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/metafields#yih4_" />
        </>
      }
    >
      {customer.parent_customer_id && (
        <Alert
          headingLevel="h2"
          title={t(
            `features:customers.pages.customer_details_page.company_user_alert.${customer.role === 'limited-customer' ? 'limited' : 'main'}.title`,
          )}
          variant="info"
        >
          <span>
            <Trans
              ns="features"
              i18nKey={`customers.pages.customer_details_page.company_user_alert.${customer.role === 'limited-customer' ? 'limited' : 'main'}.message`}
            />{' '}
            <LearnMoreLink link="https://docs.sparklayer.io/company-users" />
          </span>
        </Alert>
      )}
      <Card>
        <Card.Header headingLevel="h2">
          {t('features:customers.pages.customer_details_page.information.title')}
        </Card.Header>
        <Card.Body>
          <TwoColumnLayout>
            <TwoColumnLayout.Column>
              <Text>{t('features:customers.pages.customer_details_page.information.email')}</Text>
              <Text>{t('features:customers.pages.customer_details_page.information.company')}</Text>
              <Text>
                {t(
                  'features:customers.pages.customer_details_page.information.customer_group_slug',
                )}
              </Text>
              <Text>
                {t('features:customers.pages.customer_details_page.information.price_lists', {
                  length: customer.price_lists.length,
                })}
              </Text>
              <Text>
                {t(
                  'features:customers.pages.customer_details_page.information.sales_agent_groups',
                  { length: customer.sales_agent_groups.length },
                )}
              </Text>
            </TwoColumnLayout.Column>
            <TwoColumnLayout.Column>
              <OptionalDetail value={customer.email} />
              <OptionalDetail value={customer.company_name} />
              <OptionalDetail value={customerGroups.find((g) => g.slug === customer.group)?.name} />
              <OptionalDetail value={customer.price_lists.join(', ')} />
              <OptionalDetail value={customer.sales_agent_groups.join(', ')} />
            </TwoColumnLayout.Column>
          </TwoColumnLayout>
        </Card.Body>
      </Card>
      <CustomerFinancialsCard
        paymentOnAccount={customer.payment_on_account}
        discountPercentage={customer.discount_percentage}
        accountingId={customer.accounting_id}
      />
      {!customer.parent_customer_id && (
        <>
          <CustomerRoleSelectionCard
            sparkId={customer.id}
            initialRole={customer.role}
            initialSalesAgentGroups={customer.sales_agent_groups}
          />
          <CustomerCompanyUsersCard sparkId={customer.id} />
        </>
      )}
      <CustomerAddressesCard addresses={customer.addresses} />
      <CustomerIdentifierCard
        platformId={customer.external_id}
        legacyId={customer.legacy_id}
        internalId={customer.id}
      />

      <StaffOnly>
        <CustomerAdvancedDetailsCard customer={customer} />
      </StaffOnly>
    </Page>
  );
}
